import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Text,
  Stack,
  Title,
  Group,
  Badge,
  Card,
  Grid,
  List as MantineList,
  ThemeIcon,
  LoadingOverlay,
} from '@mantine/core';
import {
  type EmployeeStatistics,
  type EmployeePersonalStats,
} from '@/schemas/API/POST/statistics/employees/response';
import { PieChart, BarChart } from '@mantine/charts';
import { List } from '../List/List';
import { TextHeader } from '../List/Header/TextHeader';
import { colors } from '@/utils/colors';
import { type Filter } from '@/schemas/API/POST/statistics/request';
import { type PagingOptions, useStatistics } from '@/hooks/statistics/useStatistics';
import { SortHeader } from '../List/Header/SortHeader';
import { NoDataStatistics } from './NoDataStatistics';

interface Props {
  filter: Filter;
}

const roles = new Map<string, string>([
  ['SERVICE_MANAGER', 'Vedoucí služby'],
  ['COORDINATOR', 'Koordinátor'],
  ['SOCIAL_WORKER', 'Sociální pracovník'],
  ['ADMIN', 'Admin'],
  ['CAREGIVER', 'Pečovatel'],
  ['CLIENT', 'Klient'],
]);

const genderMap = new Map<string, string>([
  ['FEMALE', 'žena'],
  ['MALE', 'muž'],
  ['OTHER', 'jiné'],
]);

const defaultEmployeeStatistics: EmployeeStatistics = {
  employees: {
    content: [],
    pageable: {
      paged: true,
      pageNumber: 0,
      pageSize: 10,
      offset: '0',
      unpaged: false,
      sort: {
        empty: true,
        sorted: false,
        unsorted: true,
      },
    },
    empty: 0,
    first: true,
    last: true,
    number: 0,
    numberOfElements: 0,
    size: 20,
    sort: {
      empty: true,
      sorted: false,
      unsorted: true,
    },
    totalElements: 0,
    totalPages: 0,
  },
  ages: [],
  genders: [],
  workedMinutesByService: [],
  workedMinutesByTag: [],
};

const EmployeeNameCell = (employee: EmployeePersonalStats) => (
  <Text key={employee.id}>{employee.name}</Text>
);

const EmployeeEmailCell = (employee: EmployeePersonalStats) => (
  <Text key={employee.id}>{employee.email}</Text>
);

const EmployeeRoleCell = (employee: EmployeePersonalStats) => (
  <Text key={employee.id}>{roles.get(employee.role) ?? employee.role}</Text>
);

const EmployeeAssignmentCountCell = (employee: EmployeePersonalStats) => (
  <Text key={employee.id}>{employee.assignmentCount}</Text>
);

const EmployeeWorkedHoursCell = (employee: EmployeePersonalStats) => (
  <Text key={employee.id}>{employee.workedHours}h</Text>
);

const EmployeeWageCell = (employee: EmployeePersonalStats) => (
  <Text key={employee.id}>{employee.wage} Kč</Text>
);

export const EmployeeStatisticsComponent = ({ filter }: Props): JSX.Element => {
  const navigate = useNavigate();

  const [requestParams, setRequestParams] = useState<PagingOptions>({
    page: 0,
    size: 10,
    sort: [],
  });

  const {
    data = defaultEmployeeStatistics,
    error,
    isLoading,
  } = useStatistics<EmployeeStatistics>('employees', filter, requestParams);

  const totalHours = useMemo(() => data.workedMinutesByService.reduce((sum, e) => sum + e.amount, 0) / 60, [data.workedMinutesByService]);

  const maxAgeAmount = useMemo(() => Math.max(...data.ages.map(a => a.amount)), [data.ages]);

  if (data.employees.empty && !isLoading) { // needs to be executed before the others, otherwise permanent loading
      return <NoDataStatistics />;
    }

  if (error) {
    return <Text>Chyba při načítání dat.</Text>;
  }

  const onPageChange = (pageNumber: number) => {
    setRequestParams({
      ...requestParams,
      page: pageNumber,
    });
  };

  // TODO abstract this to a standalone component @vitekva2
  let ticks: number[] = [];

  if (maxAgeAmount <= 5) {
    ticks = Array.from({ length: maxAgeAmount + 1 }, (_, i) => i);
  } else {
    const step = Math.ceil(maxAgeAmount / 5);
    ticks = Array.from(
      { length: Math.floor(maxAgeAmount / step) + 1 },
      (_, i) => i * step
    );

    if (ticks[ticks.length - 1] !== maxAgeAmount) {
      ticks.push(step * 5);
    }
  }

  const domain: [number, number] = [0, ticks[ticks.length - 1]];

  return (
    <Stack gap="lg" align="flex-start">
      <LoadingOverlay
        visible={isLoading}
        zIndex={1000}
        overlayProps={{ radius: 'sm', blur: 2 }}
        loaderProps={{ color: 'blue', type: 'bars' }}
      />
      <Grid grow justify="center" align="stretch">
        <Grid.Col span={4}>
          <Card h="100%" shadow="sm" padding="md" radius="md" withBorder>
            <Group
              justify="space-evenly"
              className="w-full h-full rounded-card"
            >
              <Stack align="center" justify="flex-start">
                <Title order={5}>Odpracovaný čas podle tagu</Title>

                <PieChart
                  data={data.workedMinutesByTag.map((item, index) => ({
                    name: item.name,
                    value: item.amount / 60,
                    color: colors[index % colors.length],
                  }))}
                  tooltipDataSource="segment"
                  withTooltip
                  tooltipAnimationDuration={200}
                  withLabelsLine
                  labelsPosition="outside"
                  strokeWidth={2}
                  size={200}
                />
              </Stack>
              <MantineList spacing="xs" size="sm" withPadding>
                {data.workedMinutesByTag.map((item, index) => (
                  <MantineList.Item
                    key={item.name}
                    icon={
                      <ThemeIcon
                        color={colors[index % colors.length]}
                        radius="xl"
                        size={10}
                      />
                    }
                  >
                    {item.name} ({Math.round(item.amount / 60)} h)
                  </MantineList.Item>
                ))}
              </MantineList>
            </Group>
          </Card>
        </Grid.Col>

        <Grid.Col span={6}>
          <Card h="100%" shadow="sm" padding="md" radius="md" withBorder>
            <Stack justify="center">
              <Title order={5}>Rozdělení podle věku</Title>
              <BarChart
                h={200}
                data={data.ages.map(a => ({
                  name: a.name,
                  počet: a.amount,
                }))}
                dataKey="name"
                series={[{ name: 'počet', color: 'blue.6' }]}
                xAxisProps={{
                  domain,
                  ticks,
                }}
                tooltipAnimationDuration={200}
                orientation="vertical"
              />
            </Stack>
          </Card>
        </Grid.Col>
        <Grid.Col span={4}>
          <Card h="100%" shadow="sm" padding="md" radius="md" withBorder>
            <Group justify="space-evenly" className="w-full">
              <Stack align="center" justify="flex-start">
                <Title order={5}>Rozdělení podle pohlaví</Title>
                <PieChart
                  data={data.genders.map((item, index) => ({
                    name: genderMap.get(item.name) ?? item.name,
                    value: item.amount,
                    color: colors[index % colors.length],
                  }))}
                  tooltipDataSource="segment"
                  withTooltip
                  tooltipAnimationDuration={200}
                  size={200}
                />
              </Stack>
              <MantineList spacing="xs" size="sm" withPadding>
                {data.genders.map((item, index) => (
                  <MantineList.Item
                    key={item.name}
                    icon={
                      <ThemeIcon
                        color={colors[index % colors.length]}
                        radius="xl"
                        size={10}
                      />
                    }
                  >
                    {genderMap.get(item.name) ?? item.name} ({item.amount} osob)
                  </MantineList.Item>
                ))}
              </MantineList>
            </Group>
          </Card>
        </Grid.Col>
        <Grid.Col span={6}>
          <Card h="100%" shadow="sm" padding="md" radius="md" withBorder>
            <Stack>
              <Title order={5}>Odpracovaný čas na jednotlivých službách</Title>
              <BarChart
                mx={20}
                h={200}
                data={data.workedMinutesByService.map(s => ({
                  name: s.name,
                  hodiny: s.amount / 60,
                }))}
                dataKey="name"
                series={[{ name: 'hodiny', color: 'violet.6' }]}
                strokeDasharray="15 15"
                p={6}
                tooltipAnimationDuration={200}
              />
              <Text fz="sm">
                Celkem: <Badge>{totalHours} h</Badge>
              </Text>
            </Stack>
          </Card>
        </Grid.Col>
      </Grid>

      <div className="h-full w-full overflow-auto">
        <List
          loading={!data}
          pagination={{
            page: Number(data.employees.pageable.pageNumber + 1),
            setPage: e => onPageChange(e),
            pageTotal: data.employees.totalPages ?? 1,
          }}
          perPageSelector={{
            label: 'Zaměstnanců na stránce',
            value: requestParams.size ?? 0,
            setter: v =>
              setRequestParams(currentParams => ({
                ...currentParams,
                size: v,
              })),
          }}
          columns={[
            <SortHeader
              label="Jméno"
              key="jmeno"
              fields={['name']}
              callback={sort => {
                setRequestParams(currentParams => ({
                  ...currentParams,
                  sort,
                }));
              }}
              currentSort={requestParams.sort ?? []}
            />,
            <TextHeader key="email">E-mail</TextHeader>,
            <TextHeader key="role">Role</TextHeader>,
            <SortHeader
              label="Počet úkolů"
              key="assignmentCount"
              fields={['assignmentCount']}
              callback={sort => {
                const newSort = sort.length ? sort : ['assignmentCount,asc'];
                setRequestParams(currentParams => ({
                  ...currentParams,
                  sort: newSort,
                }));
              }}
              currentSort={requestParams.sort ?? []}
            />,
            <SortHeader
              label="Odpracováno hodin"
              key="workedHours"
              fields={['workedHours']}
              callback={sort => {
                const newSort = sort.length ? sort : ['workedHours,asc'];
                setRequestParams(currentParams => ({
                  ...currentParams,
                  sort: newSort,
                }));
              }}
              currentSort={requestParams.sort ?? []}
            />,
            <SortHeader
              label="Výdělek"
              key="wage"
              fields={['wage']}
              callback={sort => {
                const newSort = sort.length ? sort : ['wage,asc'];
                setRequestParams(currentParams => ({
                  ...currentParams,
                  sort: newSort,
                }));
              }}
              currentSort={requestParams.sort ?? []}
            />,
          ]}
          content={{
            items: data?.employees.content ?? [],
            onClick: (employee: EmployeePersonalStats) =>
              navigate(`/employees/${employee.id}/personal`),
            renders: [
              EmployeeNameCell,
              EmployeeEmailCell,
              EmployeeRoleCell,
              EmployeeAssignmentCountCell,
              EmployeeWorkedHoursCell,
              EmployeeWageCell,
            ],
          }}
        />
      </div>
    </Stack>
  );
};
